// Centralized API service with automatic token handling and logout on auth errors
import webAuthService from "./webAuthService";
import { isMeetingActive } from "./meetingSession";

class ApiService {
  constructor() {
    this.baseURL =
      import.meta.env.VITE_API_BASE_URL || "http://localhost:8080/api";
    this.authContext = null; // Will be set by components using the auth context
  }

  // Set auth context for forced logout capability
  setAuthContext(authContext) {
    this.authContext = authContext;
  }

  // Get access token
  async getAccessToken() {
    try {
      const tokenResponse = await webAuthService.getAccessToken();
      if (tokenResponse && tokenResponse.accessToken) {
        return tokenResponse.accessToken;
      }

      throw new Error("No access token available");
    } catch (error) {
      console.error("[API] Failed to get access token:", error);
      throw error;
    }
  }

  // Handle authentication errors and auto-logout
  async handleAuthError(error, response = null) {
    const status = response?.status || error.status;

    if (status === 401 || status === 403) {
      // Never yank a rep out of a live call over one failed request. The
      // meeting WebSocket is already authenticated and keeps running; the
      // logout is handled by AuthContext's revalidation once the call ends.
      if (isMeetingActive()) {
        console.warn(
          `[API] Authentication error (${status}) during a live meeting — not logging out; will re-check after the call`,
        );
        throw error;
      }

      console.warn(`[API] Authentication error (${status}), logging out user`);

      // SSO users have no MSAL session — skip MSAL logout to avoid popup.
      const ssoRaw = localStorage.getItem("userProfileData");
      const isSSOUser = (() => {
        try { return JSON.parse(ssoRaw)?.source === "sso"; } catch { return false; }
      })();

      if (isSSOUser) {
        console.warn("[API] SSO user got auth error — clearing session and reloading");
        localStorage.removeItem("userProfileData");
        localStorage.removeItem("auth_token");
        window.location.href = "/";
        return;
      }

      // Use auth context if available for better UX
      if (this.authContext?.forceLogout) {
        await this.authContext.forceLogout(`Authentication failed (${status})`);
        return;
      }

      // Use MSAL logout
      try {
        await webAuthService.logout();
        window.location.href = "/";
      } catch (logoutError) {
        console.error("[API] Failed to logout on auth error:", logoutError);
      }
    }

    throw error;
  }

  // Generic HTTP request with automatic token injection
  async request(url, options = {}) {
    try {
      // Get access token
      const accessToken = await this.getAccessToken();

      // Prepare headers
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        ...options.headers,
      };

      // Make request
      const response = await fetch(`${this.baseURL}${url}`, {
        ...options,
        headers,
      });
      // Check for auth errors
      if (response.status === 401 || response.status === 403) {
        await this.handleAuthError(
          new Error(`HTTP ${response.status}`),
          response
        );
      }

      // Check for other errors
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Return JSON response
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        return await response.json();
      }

      return response;
    } catch (error) {
      await this.handleAuthError(error);
    }
  }

  // Convenience methods
  async get(url, options = {}) {
    return this.request(url, { ...options, method: "GET" });
  }

  async post(url, data, options = {}) {
    return this.request(url, {
      ...options,
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async put(url, data, options = {}) {
    return this.request(url, {
      ...options,
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async delete(url, options = {}) {
    return this.request(url, { ...options, method: "DELETE" });
  }

  // Meeting-specific methods
  async endMeeting(meetingId, userId, transcript) {
    const body = { meeting_id: meetingId, user_id: userId };
    if (transcript) body.transcript = transcript;
    return this.post("/end_meeting", body);
  }

  async clearSession(meetingId, userId, prospectName) {
    return this.post(`/meetings/${encodeURIComponent(meetingId)}/clear_session`, {
      user_id: userId,
      ...(prospectName ? { prospect_name: prospectName } : {}),
    });
  }

  // --- Extension API tokens (HubSpot/Trellus browser extension access) ---

  async createApiToken(label) {
    return this.post("/settings/api_tokens", label ? { label } : {});
  }

  async listApiTokens() {
    return this.get("/settings/api_tokens");
  }

  async revokeApiToken(tokenId) {
    return this.delete(`/settings/api_tokens/${encodeURIComponent(tokenId)}`);
  }

  async getMeetingSummary(meetingId) {
    return this.get(`/meetings/${meetingId}/summary`);
  }

  // Get base API URL
  getApiUrl() {
    return this.baseURL;
  }

  // Submit feedback for a suggestion
  async submitSuggestionFeedback(suggestionId, rating, feedbackText) {
    return this.post(`/feedback/suggestion/${suggestionId}`, {
      rating: rating || null,
      feedback: feedbackText || null,
    });
  }

  // Submit feedback for a follow-up question
  async submitFollowUpFeedback(questionId, meetingId, userId, rating, feedbackText) {
    return this.post(`/feedback`, {
      suggestion_id: questionId,
      meeting_id: meetingId,
      user_id: userId,
      rating: rating || null,
      feedback: feedbackText || null,
      type: "followup_question",
    });
  }

  // WebSocket connection with token in headers
  async createAuthenticatedWebSocket(url, protocols = []) {
    try {
      const accessToken = await this.getAccessToken();

      // For WebSocket, we'll pass the token as a query parameter since headers aren't always supported
      const separator = url.includes("?") ? "&" : "?";
      const authenticatedUrl = `${url}${separator}access_token=${encodeURIComponent(
        accessToken
      )}`;

      const ws = new WebSocket(authenticatedUrl, protocols);

      ws.addEventListener("error", async (event) => {
        console.error("[WEBSOCKET] WebSocket error:", event);
      });

      ws.addEventListener("close", async (event) => {
        if (event.code === 1008 || event.code === 3000) {
          console.warn("[WEBSOCKET] WebSocket closed due to authentication error");
          await this.handleAuthError(
            new Error(`WebSocket auth error: ${event.code}`)
          );
        }
      });

      return ws;
    } catch (error) {
      console.error("[API] Failed to create authenticated WebSocket:", error);
      await this.handleAuthError(error);
    }
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
